/**
 * Viewport culling for the video-editor timeline.
 * Full clip lists stay in memory; only overlapping clips are painted.
 */

export const TIMELINE_VIEWPORT_OVERSCAN_PX = 480;

/**
 * Visible time window in the clip lane (seconds), including overscan.
 * Clip `left` is relative to the flex-1 area after the sticky label column.
 */
export function timelineViewportTimeRange({
  scrollLeft = 0,
  viewportWidth = 0,
  pxPerSec = 80,
  labelWidth = 0,
  overscanPx = TIMELINE_VIEWPORT_OVERSCAN_PX,
} = {}) {
  const perSec = Math.max(1e-6, Number(pxPerSec) || 1);
  const left = Math.max(0, Number(scrollLeft) || 0);
  const vw = Math.max(0, Number(viewportWidth) || 0);
  const label = Math.max(0, Number(labelWidth) || 0);
  const pad = Math.max(0, Number(overscanPx) || 0);

  // When width is unknown (first paint), keep a wide window so we do not flash empty.
  if (vw <= 0) {
    return { startSec: 0, endSec: Number.POSITIVE_INFINITY };
  }

  const contentLeft = left - label - pad;
  const contentRight = left + vw - label + pad;
  return {
    startSec: Math.max(0, contentLeft / perSec),
    endSec: Math.max(0, contentRight / perSec),
  };
}

export function defaultTimelineClipDurationSec(clip) {
  if (!clip || typeof clip !== 'object') return 0;
  const end = Number(clip.endTime);
  const start = Number(clip.startTime) || 0;
  if (Number.isFinite(end) && end > start) return end - start;
  const candidates = [
    clip.playbackDuration,
    clip.duration,
    clip.duration_seconds,
    clip.audio_duration,
  ];
  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0.01;
}

/**
 * Keep clips that overlap [startSec, endSec], plus any forced ids (selection / drag).
 */
export function clipsInTimelineViewport(
  clips,
  {
    startSec = 0,
    endSec = Number.POSITIVE_INFINITY,
    getDuration = defaultTimelineClipDurationSec,
    forceIds = null,
    timeOffsetSec = 0,
  } = {},
) {
  const list = Array.isArray(clips) ? clips : [];
  if (!list.length) return list;
  if (!Number.isFinite(endSec) && startSec <= 0 && !forceIds?.size) return list;

  const force = forceIds instanceof Set
    ? forceIds
    : new Set(Array.isArray(forceIds) ? forceIds : forceIds ? [forceIds] : []);
  const offset = Number(timeOffsetSec) || 0;
  const rangeStart = Number(startSec) || 0;
  const rangeEnd = Number.isFinite(endSec) ? endSec : Number.POSITIVE_INFINITY;

  return list.filter((clip) => {
    if (!clip) return false;
    if (force.has(clip.id) || (clip.sourceClipId && force.has(clip.sourceClipId))) {
      return true;
    }
    const start = (Number(clip.startTime) || 0) + offset;
    const dur = Math.max(0, Number(getDuration(clip)) || 0);
    const end = start + dur;
    return end >= rangeStart && start <= rangeEnd;
  });
}

/** Integer second marks for the sticky time ruler within the viewport. */
export function timeMarksInTimelineViewport({
  startSec = 0,
  endSec = 0,
  totalDuration = 0,
} = {}) {
  const total = Math.max(0, Math.ceil(Number(totalDuration) || 0));
  if (total <= 0) return [0];
  if (!Number.isFinite(endSec)) {
    return Array.from({ length: total + 1 }, (_, i) => i);
  }
  const from = Math.max(0, Math.floor(Number(startSec) || 0));
  const to = Math.min(total, Math.ceil(Number(endSec) || 0));
  const marks = [];
  for (let i = from; i <= to; i += 1) marks.push(i);
  return marks.length ? marks : [from];
}
